/**
 * Abre o anexo de uma requisição DENTRO do ERP (2026-10-02), como na Gestão
 * de Solicitações — antes o link ia para a URL do S3 numa aba nova.
 *
 * - Imagem: o mesmo `ImagemViewer` da Gestão (zoom, imprimir, baixar,
 *   compartilhar), só leitura — anexo de compra não se edita.
 * - PDF, planilha, Word, texto: `DocumentViewerModal`.
 *
 * O arquivo vem do proxy autenticado do backend (`download_url`): o bucket
 * não tem CORS, então o navegador não consegue ler a URL assinada do S3 —
 * e o visualizador precisa ler o arquivo para mostrar (e para baixar).
 */
import React, { useCallback, useEffect, useState } from "react";

import DocumentViewerModal from "../../../components/DocumentViewerModal";
import ImagemViewer from "../../../components/ImagemViewer";
import { MiniSpinner } from "../../../components/LoadingMarcenaria";
import { useAuth } from "../../../context/AuthContext";
import { buscarArquivoComAuth, detectarKind } from "../../../utils/documentos";
import { ModalOverlay } from "./ui";

function blobParaDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(leitor.result);
    leitor.onerror = () => reject(leitor.error || new Error("Não foi possível ler a imagem."));
    leitor.readAsDataURL(blob);
  });
}

/** `anexo`: { id, nome, download_url, url }; `cabecalho`: usado na impressão. */
export default function VisualizadorDeAnexo({ anexo, cabecalho, onFechar }) {
  const { request } = useAuth();
  const caminho = anexo?.download_url || anexo?.url || "";
  const ehImagem = detectarKind({ nome: anexo?.nome, url: caminho }) === "image";
  const [imagem, setImagem] = useState(null);
  // Imagem que não carregou cai no visualizador de documentos, que mostra o
  // erro do backend ("Arquivo indisponível…") com o botão de baixar.
  const [caiuParaDocumento, setCaiuParaDocumento] = useState(false);

  const renovarSessao = useCallback(() => request("/api/me", { silentError: true }), [request]);

  useEffect(() => {
    if (!ehImagem || !caminho) return undefined;
    const controle = new AbortController();
    buscarArquivoComAuth(caminho, { signal: controle.signal, renovarSessao })
      .then(blobParaDataUrl)
      .then(setImagem)
      .catch((e) => { if (e?.name !== "AbortError") setCaiuParaDocumento(true); });
    return () => controle.abort();
  }, [ehImagem, caminho, renovarSessao]);

  if (!anexo) return null;

  if (!ehImagem || caiuParaDocumento) {
    return <DocumentViewerModal doc={{ id: anexo.id, nome: anexo.nome, url: caminho }} onClose={onFechar} />;
  }
  if (!imagem) {
    return (
      <ModalOverlay onMouseDown={(e) => { if (e.target === e.currentTarget) onFechar?.(); }}>
        <MiniSpinner size={22} label="Abrindo imagem" />
      </ModalOverlay>
    );
  }
  return (
    <ImagemViewer
      foto={{ id: anexo.id || anexo.nome, imagem, legenda: anexo.nome, editada: false }}
      cabecalho={cabecalho}
      onFechar={onFechar}
    />
  );
}
